import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, History, ChevronLeft } from "lucide-react";

// Copies a prior engagement's request-list structure into this engagement.
export function RollForwardDialog({ engagementId, open, onOpenChange, onDone }) {
  const [step, setStep] = useState("sources"); // sources | preview
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceId, setSourceId] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/m1/engagements/${engagementId}/roll-forward/sources`);
      setSources(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [engagementId]);

  useEffect(() => {
    if (open) { setStep("sources"); setSourceId(null); setItems([]); setSelected({}); loadSources(); }
  }, [open, loadSources]);

  const pickSource = async (sid) => {
    setSourceId(sid); setLoading(true);
    try {
      const r = await api.post(`/m1/engagements/${engagementId}/roll-forward/preview`, { source_id: sid });
      const its = r.data.items || [];
      setItems(its);
      // preselect non-duplicates by index
      const sel = {}; its.forEach((it, i) => { sel[i] = !it.duplicate; });
      setSelected(sel);
      setStep("preview");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const apply = async () => {
    const chosen = items.filter((_, i) => selected[i]);
    if (chosen.length === 0) return toast.error("Select at least one request to copy");
    setLoading(true);
    try {
      const r = await api.post(`/m1/engagements/${engagementId}/roll-forward/apply`, {
        source_id: sourceId,
        items: chosen.map((it) => ({
          name: it.name, description: it.description, category_name: it.category_name,
          assigned_internal: it.assigned_internal, restricted: it.restricted,
          file_visibility: it.file_visibility,
        })),
      });
      toast.success(`Rolled forward ${r.data.created} request(s)`);
      onOpenChange(false); onDone && onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="roll-forward-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-500" /> Roll forward requests
          </DialogTitle>
          <DialogDescription>Copy a prior engagement's request structure into this one. Files, statuses and due dates are not copied.</DialogDescription>
        </DialogHeader>

        {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

        {!loading && step === "sources" && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">Copy the request structure from a previous engagement you have access to. Statuses, due dates, files and client assignments are not copied.</p>
            {sources.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No other engagements available to roll forward from.</p>}
            {sources.map((s) => (
              <button key={s.id} data-testid={`rf-source-${s.id}`} onClick={() => pickSource(s.id)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-left hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div>
                  <div className="text-sm font-semibold">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.client_org_name} · {s.request_count} request(s)</div>
                </div>
                {s.same_client && <span className="rounded-full bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-600">Same client</span>}
              </button>
            ))}
          </div>
        )}

        {!loading && step === "preview" && (
          <div className="space-y-3">
            <button onClick={() => setStep("sources")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="rf-back">
              <ChevronLeft className="h-3.5 w-3.5" /> Choose a different source
            </button>
            <p className="text-sm text-slate-500">{items.length} request(s) found. Deselect any you don't want. Items already in this engagement are marked and unchecked by default.</p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800 p-2">
              {items.map((it, i) => (
                <label key={it.id || `${it.name}-${i}`} data-testid={`rf-item-${i}`} className="flex items-start gap-2.5 rounded-md p-2 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected({ ...selected, [i]: !!v })} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{it.name}</span>
                      {it.duplicate && <span className="rounded bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600">Already exists</span>}
                    </div>
                    {it.category_name && <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-500">{it.category_name}</span>}
                    {it.assigned_internal_names?.length > 0 && (
                      <div className="text-[11px] text-slate-400">Assignees: {it.assigned_internal_names.join(", ")}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {!loading && step === "preview" && (
          <DialogFooter>
            <Button onClick={apply} data-testid="rf-apply" className="bg-indigo-600 hover:bg-indigo-700">
              Roll forward {selectedCount} request(s)
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
